import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Clock } from "lucide-react";

// Relative-time helper — kept local so we don't bloat lib/format.js
// with a one-off. Returns "2m ago", "3h ago", "5d ago". Falls back to
// the raw ISO string if parsing fails.
function relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch (_) { return iso; }
}

/**
 * Compact 3-row panel that reports the last-run status of every
 * platform cron job. Data source: ``GET /commissions/cron-health``
 * which reads the latest row per job from ``cron_runs``.
 *
 * Rendering rules per job:
 *   - Green tick + ISO timestamp + brief summary  — ran ok recently
 *   - Amber warning triangle                       — either never ran
 *     or ran longer ago than the schedule allows (``stale=true``)
 *   - Red exclamation                              — last run failed
 *
 * Renders nothing while loading so the dashboard doesn't jitter.
 * Silently degrades (returns null) if the endpoint 404s / errors.
 */
export default function CronHealthPanel({ testid = "cron-health-panel" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/commissions/cron-health");
        setData(data);
      } catch (_) { /* silently degrade */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!data || !data.jobs) return null;

  return (
    <section
      data-testid={testid}
      className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
        <Clock className="w-4 h-4 text-cyan-300" />
        <div className="text-sm text-slate-100 font-semibold">Scheduled jobs</div>
        <div className="text-[11px] text-slate-500">Platform crons · last-run status</div>
      </header>
      <ul className="divide-y divide-slate-800">
        {data.jobs.map((j) => {
          const failed = j.last_ok === false;
          const stale = !!j.stale;
          const iconColor = failed
            ? "text-red-400"
            : stale
              ? "text-amber-400"
              : "text-emerald-400";
          const Icon = failed
            ? AlertTriangle
            : stale
              ? AlertTriangle
              : CheckCircle2;
          const statusLabel = failed
            ? "Last run failed"
            : stale
              ? (j.last_ran_at ? "Overdue" : "Never run")
              : "Healthy";
          const summary = j.last_summary || {};
          const parts = Object.entries(summary).slice(0, 4).map(([k, v]) => `${k}: ${v}`);
          return (
            <li
              key={j.job}
              data-testid={`${testid}-row-${j.job}`}
              className="px-4 py-3 flex items-start gap-3"
            >
              <Icon className={`w-4 h-4 mt-0.5 ${iconColor} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm text-slate-100 font-medium font-mono">{j.job}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${iconColor}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{j.description}</div>
                <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>schedule <span className="font-mono text-slate-500">{j.schedule}</span></span>
                  {j.last_ran_at && (
                    <span data-testid={`${testid}-row-${j.job}-last`}>
                      last ran <span className="text-slate-400">{relTime(j.last_ran_at)}</span>
                    </span>
                  )}
                  {!j.last_ran_at && (
                    <span className="text-amber-300">no runs recorded yet</span>
                  )}
                </div>
                {parts.length > 0 && (
                  <div className="text-[11px] text-slate-500 mt-1 font-mono">
                    {parts.join(" · ")}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
